import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as echarts from 'echarts';

import { ApiService } from '../../core/api.service';

type CashFlowType = 'INCOME' | 'EXPENSE';
type CashFlowSource = 'ORDER' | 'MANUAL' | 'SYSTEM';
type CashFlowViewMode = 'SUMMARY' | 'DETAILED';

interface Restaurant {
  id: number;
  name: string;
}

interface CashFlowEntry {
  id: number;
  restaurantId: number;
  orderId?: number | null;
  type: CashFlowType;
  source: CashFlowSource;
  category: string;
  description: string;
  amount: number;
  entryDate: string;
}

interface CashFlowSummary {
  totalIncome: number;
  totalExpense: number;
  balance: number;
  entries: number;
}

interface CashFlowForm {
  id?: number;
  type: CashFlowType;
  category: string;
  description: string;
  amount: number | null;
  entryDate: string;
}

interface CashFlowGroup {
  key: string;
  date: string;
  description: string;
  category: string;
  type: CashFlowType;
  source: CashFlowSource;
  amount: number;
  orderCount: number;
  entries: CashFlowEntry[];
  expanded: boolean;
}

@Component({
  selector: 'app-cash-flow',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './cash-flow.component.html',
  styleUrl: './cash-flow.component.css'
})
export class CashFlowComponent
  implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('cashFlowChart')
  cashFlowChartElement?: ElementRef<HTMLDivElement>;

  restaurants: Restaurant[] = [];
  entries: CashFlowEntry[] = [];
  groupedEntries: CashFlowGroup[] = [];

  restaurantId: number | null = null;
  viewMode: CashFlowViewMode = 'SUMMARY';

  startDate = '';
  endDate = '';
  selectedPeriod = 'month';
  typeFilter: '' | CashFlowType = '';

  summary: CashFlowSummary = {
    totalIncome: 0,
    totalExpense: 0,
    balance: 0,
    entries: 0
  };

  form: CashFlowForm = this.createEmptyForm();

  loadingRestaurants = false;
  loadingData = false;
  saving = false;
  deletingId: number | null = null;

  modalOpen = false;
  error = '';
  success = '';
  viewInitialized = false;

  private chart?: echarts.ECharts;

  constructor(private api: ApiService) {}

  ngOnInit(): void {
    this.setPeriod('month', false);
    this.loadRestaurants();
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    setTimeout(() => this.renderChart());
  }

  ngOnDestroy(): void {
    this.chart?.dispose();
  }

  loadRestaurants(): void {
    this.loadingRestaurants = true;
    this.error = '';

    this.api.get<Restaurant[]>('/restaurants').subscribe({
      next: restaurants => {
        this.restaurants = restaurants || [];
        this.loadingRestaurants = false;

        if (this.restaurants.length) {
          this.restaurantId = this.restaurants[0].id;
          this.loadData();
        }
      },
      error: error => {
        this.loadingRestaurants = false;
        this.error =
          error?.error?.message ||
          'Erro ao carregar os restaurantes.';
      }
    });
  }

  loadData(): void {
    if (!this.restaurantId || !this.startDate || !this.endDate) {
      return;
    }

    this.loadingData = true;
    this.error = '';
    this.success = '';

    let pendingRequests = 2;

    const finishRequest = (): void => {
      pendingRequests -= 1;

      if (pendingRequests === 0) {
        this.loadingData = false;
        setTimeout(() => this.renderChart());
      }
    };

    this.api
      .get<CashFlowEntry[]>(`/cash-flow?${this.buildQuery()}`)
      .subscribe({
        next: entries => {
          this.entries = (entries || []).map(entry => ({
            ...entry,
            amount: Number(entry.amount || 0)
          }));

          this.groupEntriesByDay();
          finishRequest();
        },
        error: error => {
          this.entries = [];
          this.groupedEntries = [];
          this.error =
            error?.error?.message ||
            'Erro ao carregar os lançamentos.';

          finishRequest();
        }
      });

    this.api
      .get<{ summary: CashFlowSummary }>(
        `/cash-flow/summary?${this.buildQuery(false)}`
      )
      .subscribe({
        next: response => {
          this.summary = {
            totalIncome: Number(response?.summary?.totalIncome || 0),
            totalExpense: Number(response?.summary?.totalExpense || 0),
            balance: Number(response?.summary?.balance || 0),
            entries: Number(response?.summary?.entries || 0)
          };

          finishRequest();
        },
        error: error => {
          this.summary = {
            totalIncome: 0,
            totalExpense: 0,
            balance: 0,
            entries: 0
          };

          this.error =
            error?.error?.message ||
            'Erro ao carregar o resumo financeiro.';

          finishRequest();
        }
      });
  }

  private groupEntriesByDay(): void {
    const orderGroups = new Map<string, CashFlowGroup>();
    const individualEntries: CashFlowGroup[] = [];

    this.entries.forEach(entry => {
      const date = this.normalizeDate(entry.entryDate);

      const shouldGroup =
        entry.source === 'ORDER' &&
        entry.type === 'INCOME';

      if (shouldGroup) {
        const key = `ORDER-${date}`;
        const existingGroup = orderGroups.get(key);

        if (existingGroup) {
          existingGroup.amount += Number(entry.amount || 0);
          existingGroup.orderCount += 1;
          existingGroup.entries.push(entry);
          return;
        }

        orderGroups.set(key, {
          key,
          date,
          description: 'Vendas do dia',
          category: 'Vendas',
          type: 'INCOME',
          source: 'ORDER',
          amount: Number(entry.amount || 0),
          orderCount: 1,
          entries: [entry],
          expanded: false
        });

        return;
      }

      individualEntries.push({
        key: `ENTRY-${entry.id}`,
        date,
        description: entry.description,
        category: entry.category,
        type: entry.type,
        source: entry.source,
        amount: Number(entry.amount || 0),
        orderCount: 0,
        entries: [entry],
        expanded: false
      });
    });

    this.groupedEntries = [
      ...Array.from(orderGroups.values()),
      ...individualEntries
    ].sort((first, second) => {
      const dateComparison = second.date.localeCompare(first.date);

      if (dateComparison !== 0) {
        return dateComparison;
      }

      return second.key.localeCompare(first.key);
    });
  }

  private buildQuery(includeType = true): string {
    const params = new URLSearchParams({
      restaurantId: String(this.restaurantId),
      startDate: this.startDate,
      endDate: this.endDate
    });

    if (includeType && this.typeFilter) {
      params.set('type', this.typeFilter);
    }

    return params.toString();
  }

  setPeriod(period: string, reload = true): void {
    this.selectedPeriod = period;

    const today = this.createLocalDate();
    let start = this.createLocalDate();
    let end = this.createLocalDate();

    switch (period) {
      case 'today':
        break;

      case '7days':
        start.setDate(start.getDate() - 6);
        break;

      case '30days':
        start.setDate(start.getDate() - 29);
        break;

      case 'month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;

      case 'custom':
        return;
    }

    this.startDate = this.formatDateInput(start);
    this.endDate = this.formatDateInput(end);

    if (reload && this.restaurantId) {
      this.loadData();
    }
  }

  applyCustomPeriod(): void {
    this.error = '';

    if (!this.startDate || !this.endDate) {
      this.error = 'Informe a data inicial e a data final.';
      return;
    }

    if (this.startDate > this.endDate) {
      this.error = 'A data inicial não pode ser maior que a data final.';
      return;
    }

    this.selectedPeriod = 'custom';
    this.loadData();
  }

  onRestaurantChange(): void {
    this.loadData();
  }

  onTypeFilterChange(): void {
    this.loadData();
  }

  setViewMode(mode: CashFlowViewMode): void {
    this.viewMode = mode;
  }

  toggleGroup(group: CashFlowGroup): void {
    if (group.source !== 'ORDER') {
      return;
    }

    group.expanded = !group.expanded;
  }

  openCreateModal(type: CashFlowType = 'EXPENSE'): void {
    this.form = {
      ...this.createEmptyForm(),
      type
    };

    this.error = '';
    this.success = '';
    this.modalOpen = true;
  }

  openEditModal(entry: CashFlowEntry): void {
    if (entry.source !== 'MANUAL') {
      this.error =
        'Apenas lançamentos manuais podem ser editados.';
      return;
    }

    this.form = {
      id: entry.id,
      type: entry.type,
      category: entry.category,
      description: entry.description,
      amount: Number(entry.amount),
      entryDate: this.normalizeDate(entry.entryDate)
    };

    this.modalOpen = true;
  }

  closeModal(): void {
    if (this.saving) {
      return;
    }

    this.modalOpen = false;
    this.form = this.createEmptyForm();
  }

  saveEntry(): void {
    this.error = '';
    this.success = '';

    if (!this.restaurantId) {
      this.error = 'Selecione um restaurante.';
      return;
    }

    if (
      !this.form.category.trim() ||
      !this.form.description.trim() ||
      !this.form.entryDate ||
      !this.form.amount ||
      Number(this.form.amount) <= 0
    ) {
      this.error = 'Preencha todos os campos corretamente.';
      return;
    }

    const body = {
      restaurantId: this.restaurantId,
      type: this.form.type,
      category: this.form.category.trim(),
      description: this.form.description.trim(),
      amount: Number(this.form.amount),
      entryDate: this.form.entryDate
    };

    this.saving = true;

    const editing = Boolean(this.form.id);

    const request = this.form.id
      ? this.api.put<CashFlowEntry>(
          `/cash-flow/${this.form.id}`,
          body
        )
      : this.api.post<CashFlowEntry>('/cash-flow', body);

    request.subscribe({
      next: () => {
        this.saving = false;
        this.modalOpen = false;
        this.form = this.createEmptyForm();

        this.success = editing
          ? 'Lançamento atualizado com sucesso.'
          : 'Lançamento cadastrado com sucesso.';

        this.loadData();
      },
      error: error => {
        this.saving = false;
        this.error =
          error?.error?.message ||
          'Erro ao salvar o lançamento.';
      }
    });
  }

  deleteEntry(entry: CashFlowEntry): void {
    if (entry.source !== 'MANUAL') {
      this.error =
        'Apenas lançamentos manuais podem ser excluídos.';
      return;
    }

    const confirmed = window.confirm(
      `Excluir "${entry.description}"?`
    );

    if (!confirmed) {
      return;
    }

    this.deletingId = entry.id;

    this.api.delete<void>(`/cash-flow/${entry.id}`).subscribe({
      next: () => {
        this.deletingId = null;
        this.success = 'Lançamento excluído com sucesso.';
        this.loadData();
      },
      error: error => {
        this.deletingId = null;
        this.error =
          error?.error?.message ||
          'Erro ao excluir o lançamento.';
      }
    });
  }

  private createEmptyForm(): CashFlowForm {
    return {
      type: 'EXPENSE',
      category: '',
      description: '',
      amount: null,
      entryDate: this.formatDateInput(this.createLocalDate())
    };
  }

  private renderChart(): void {
    if (!this.viewInitialized) {
      return;
    }

    const element = this.cashFlowChartElement?.nativeElement;

    if (!element) {
      return;
    }

    this.chart?.dispose();
    this.chart = echarts.init(element);

    const grouped = new Map<
      string,
      { income: number; expense: number }
    >();

    this.entries.forEach(entry => {
      const date = this.normalizeDate(entry.entryDate);
      const current = grouped.get(date) || {
        income: 0,
        expense: 0
      };

      if (entry.type === 'INCOME') {
        current.income += Number(entry.amount || 0);
      } else {
        current.expense += Number(entry.amount || 0);
      }

      grouped.set(date, current);
    });

    const dates = Array.from(grouped.keys()).sort();

    this.chart.setOption({
      tooltip: {
        trigger: 'axis',
        valueFormatter: (value: unknown) =>
          this.formatMoney(Number(value || 0))
      },
      legend: {
        data: ['Entradas', 'Saídas']
      },
      grid: {
        left: 20,
        right: 20,
        top: 50,
        bottom: 25,
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: dates.map(date => this.formatShortDate(date))
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: number) =>
            this.formatCompactMoney(value)
        }
      },
      series: [
        {
          name: 'Entradas',
          type: 'bar',
          data: dates.map(
            date => grouped.get(date)?.income || 0
          )
        },
        {
          name: 'Saídas',
          type: 'bar',
          data: dates.map(
            date => grouped.get(date)?.expense || 0
          )
        }
      ]
    });
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.chart?.resize();
  }

  trackByEntryId(
    _index: number,
    entry: CashFlowEntry
  ): number {
    return entry.id;
  }

  trackByGroupKey(
    _index: number,
    group: CashFlowGroup
  ): string {
    return group.key;
  }

  getTypeLabel(type: CashFlowType): string {
    return type === 'INCOME' ? 'Entrada' : 'Saída';
  }

  getSourceLabel(source: CashFlowSource): string {
    const labels: Record<CashFlowSource, string> = {
      ORDER: 'Pedido',
      MANUAL: 'Manual',
      SYSTEM: 'Sistema'
    };

    return labels[source] || source;
  }

  getBalanceClass(): string {
    if (this.summary.balance > 0) {
      return 'positive';
    }

    if (this.summary.balance < 0) {
      return 'negative';
    }

    return 'neutral';
  }

  formatMoney(value: unknown): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(Number(value || 0));
  }

  formatCompactMoney(value: number): string {
    const number = Number(value || 0);

    if (Math.abs(number) >= 1_000_000) {
      return `R$ ${(number / 1_000_000)
        .toFixed(1)
        .replace('.', ',')} mi`;
    }

    if (Math.abs(number) >= 1_000) {
      return `R$ ${(number / 1_000)
        .toFixed(1)
        .replace('.', ',')} mil`;
    }

    return `R$ ${number.toFixed(0)}`;
  }

  formatDate(value: string): string {
    const date = this.normalizeDate(value);

    if (!date) {
      return '-';
    }

    const [year, month, day] = date.split('-');
    return `${day}/${month}/${year}`;
  }

  formatShortDate(value: string): string {
    const date = this.normalizeDate(value);

    if (!date) {
      return '';
    }

    const [, month, day] = date.split('-');
    return `${day}/${month}`;
  }

  private normalizeDate(value: string): string {
    if (!value) {
      return '';
    }

    return value.includes('T')
      ? value.split('T')[0]
      : value.substring(0, 10);
  }

  private createLocalDate(): Date {
    const now = new Date();

    return new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
  }

  private formatDateInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }
}